import * as React from 'react';

import { AuthProvider as GrayAuthProvider, useAuth } from './hooks/useAuth';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { createContext, useMemo, useState } from 'react';

import AppRedirect from './AppRedirect';
import { Box } from '@mui/material';
import Buzzer from './components/fabcon/Shell';
import FullPageLoader from './components/system-components/loaders/FullPageLoader';
import Home from './components/home/Home';
import LandingPage from './components/system-components/landing-page/LandingPage';
import { Suspense } from 'react';
import appInsights from "./appinsights";
import isJwtExpired from './utilities/isJwtExpired';
import { useLocalStorage } from './hooks/useLocalStorage';
import Debug from './components/system-components/Debug';
import PrivacyPage from './components/public/PrivacyPage';
import TermsOfServicePage from './components/public/TOS';
import DataDeletePage from './components/public/DataDeletePage';
import NotFoundPage from './components/public/NotFoundPage';

// Create a context to provide the toggle function throughout the app.
export const ColorModeContext = createContext({
  toggleColorMode: () => { },
});

// ProtectedRoute remains unchanged.
const ProtectedRoute = ({ children, requiresAdmin = false }) => {
  const { user, userDetails, logout } = useAuth();
  const location = useLocation();
  // eslint-disable-next-line no-unused-vars
  const [_, setUser] = useLocalStorage("jwt", null);
  const pathName = location.pathname;

  if (!user || isJwtExpired(user)) {
    localStorage.setItem('lastPath', pathName);
    return <Navigate to="/login" replace />;
  } else {
    const { id } = userDetails();
    appInsights.setAuthenticatedUserContext(id, 'google');
  }

  if (requiresAdmin) {
    const { role } = userDetails();
    if (role !== 'admin') {
      setUser(null);
      logout().then(() => {
        console.log("logged out");
      }).catch((error) => {
        console.error(error);
      });
      return <Navigate to="/login" replace />;
    }
  }

  localStorage.removeItem('lastPath');
  return children;
};

export default function App() {
  // Use local storage or state to persist the theme mode.
  // Here we default to 'light', but you could use your useLocalStorage hook instead.
  const [mode, setMode] = useState('dark');

  // Memoize the theme to avoid unnecessary recalculations.
  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode,
        },
      }),
    [mode]
  );

  // Provide a toggle function via context.
  const colorMode = useMemo(
    () => ({
      toggleColorMode: () => {
        setMode((prevMode) => (prevMode === 'light' ? 'dark' : 'light'));
      },
    }),
    []
  );

  const boxedLoader = (
    <Box sx={{ height: 'var(--app-height)' }}>
      <FullPageLoader />
    </Box>
  );

  return (
    <ColorModeContext.Provider value={colorMode}>
      <ThemeProvider theme={theme}>
        <GrayAuthProvider>
          <Routes>

            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <Navigate to="/redirect" replace />
                </ProtectedRoute>
              }
            />
            <Route
              exact
              path="/redirect"
              element={
                <ProtectedRoute>
                  <Suspense fallback={<FullPageLoader />}>
                    <AppRedirect />
                  </Suspense>
                </ProtectedRoute>
              }
            />
            <Route
              exact
              path="/home"
              element={
                <ProtectedRoute>
                  <Suspense fallback={boxedLoader}>
                    <Home />
                  </Suspense>
                </ProtectedRoute>
              }
            />
            <Route
              exact
              path="/landing"
              element={
                <Suspense fallback={boxedLoader}>
                  <LandingPage />
                </Suspense>
              }
            />
            <Route
              exact
              path="/login/:section?"
              element={
                <Suspense fallback={boxedLoader}>
                  <LandingPage />
                </Suspense>
              }
            />
            <Route
              exact
              path="/fabcon"
              element={
                <Suspense fallback={boxedLoader}>
                  <Buzzer />
                </Suspense>
              }
            />
            <Route
              exact
              path="/debug"
              element={
                <Suspense fallback={boxedLoader}>
                  <Debug />
                </Suspense>
              }
            />
            <Route exact path="/privacy"
              element={<Suspense fallback={boxedLoader}>
                <PrivacyPage />
              </Suspense>}>
            </Route>
            <Route exact path="/terms-of-service"
              element={<Suspense fallback={boxedLoader}>
                <TermsOfServicePage />
              </Suspense>}>
            </Route>
            <Route exact path="/data-delete"
              element={<Suspense fallback={boxedLoader}>
                <DataDeletePage />
              </Suspense>}>
            </Route>
            <Route path="*" element={<Suspense fallback={boxedLoader}><NotFoundPage /></Suspense>}></Route>
          </Routes>
        </GrayAuthProvider>
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
}
